// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for creating a new accounting close run.
 * Provides type-safe close run creation with Zod validation.
 */
import * as z from 'zod';
import { closeRunResponseSchema } from '@/accounting/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the response from the create close run API endpoint.
 * Includes both the close run data and metadata about the request.
 */
const createCloseRunResponseSchema = z
  .object({
    data: closeRunResponseSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, closeRun: data }));

/**
 * Type definition for the create close run API response.
 * Contains the created close run data and request metadata.
 */
type CreateCloseRunResponse = z.infer<typeof createCloseRunResponseSchema>;

/**
 * Parameters required to create a new close run.
 * @property year - The year of the accounting period
 * @property month - The month of the accounting period (1-12)
 */
interface CreateCloseRunParameters {
  year: number;
  month: number;
}

/**
 * Creates a new accounting close run for the specified period.
 *
 * @param parameters - Object containing the year and month for the close run
 * @returns Promise resolving to the created close run data and request metadata
 * @throws Will throw an error if the API request fails or response validation fails
 */
export async function createCloseRun(
  parameters: CreateCloseRunParameters
): Promise<CreateCloseRunResponse> {
  const url = '/v1/accounting/close-runs';
  const response = await api.post(url, parameters);

  return parseResponse(createCloseRunResponseSchema, response.data, {
    schemaName: 'CreateCloseRun',
    endpoint: url,
  });
}
